import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import withAuth from "../withAuth";
import axios from "axios";
import { handleAuthError } from "../../utils";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Table,
  Button,
  Spinner,
  Input,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';

const AllChildrenList = () => {
  document.title = "All Children List | RYD Admin";

  const [children, setChildren] = useState([]);
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchAllChildren();
  }, []);

  useEffect(() => {
    if (searchQuery === "") {
      setFilteredChildren(children);
    } else {
      const filtered = children.filter(child => 
        child.childName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        child.parentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        child.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        child.packageTitle.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredChildren(filtered);
    }
  }, [searchQuery, children]);

  const fetchAllChildren = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/admin/child/list`);
      if (response.data.status) {
        setChildren(response.data.data);
        setFilteredChildren(response.data.data);
      } else {
        setChildren([]);
        setFilteredChildren([]);
        toast.info("No children found");
      }
      setLoading(false);
    } catch (error) {
      handleAuthError(error);
      console.error(error);
      toast.error("Failed to fetch children");
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (filteredChildren.length === 0) {
      toast.warning("No data to download");
      return;
    }

    // CSV headers
    const headers = ["Child Name", "Parent Name", "Email Address", "Package Title", "Level", "Registration Status"];
    
    // CSV rows
    const rows = filteredChildren.map(child => [
      child.childName || "N/A",
      child.parentName || "N/A",
      child.email || "N/A",
      child.packageTitle || "N/A",
      child.level || "N/A",
      child.registrationStatus || "N/A"
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `all_children_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("CSV file downloaded successfully");
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="All Children List" breadcrumbItem="All Children List" />

          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h4 className="card-title mb-0">All Children List ({filteredChildren.length})</h4>
                    <div className="d-flex gap-2 flex-wrap">
                      <Input
                        type="text"
                        placeholder="Search by name, email, or package..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ width: '300px' }}
                      />
                      <Button
                        color="primary"
                        onClick={downloadCSV}
                        disabled={loading || filteredChildren.length === 0}
                      >
                        <i className="mdi mdi-download me-1"></i> Download CSV
                      </Button>
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2">Loading children...</p>
                    </div>
                  ) : filteredChildren.length === 0 ? (
                    <div className="text-center py-5">
                      <p className="text-muted">
                        {searchQuery ? "No children found matching your search." : "No children found."}
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-striped table-bordered">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Child Name</th>
                            <th>Parent Name</th>
                            <th>Email Address</th>
                            <th>Package Title</th>
                            <th>Level</th>
                            <th>Registration Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredChildren.map((child, index) => (
                            <tr key={child.id || index}>
                              <td>{index + 1}</td>
                              <td>{child.childName || "N/A"}</td>
                              <td>{child.parentName || "N/A"}</td>
                              <td>{child.email || "N/A"}</td>
                              <td>{child.packageTitle || "N/A"}</td>
                              <td>{child.level || "N/A"}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    child.registrationStatus === "Registered"
                                      ? "bg-success"
                                      : "bg-warning"
                                  }`}
                                >
                                  {child.registrationStatus || "N/A"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(AllChildrenList);

